import React from "react";
import {
  declareAiCapability,
  useProvideAiCapability,
} from "../../../ai-omnibar/ai-capability-registry";
import { declareAiTabDestination } from "../../../ai-omnibar/ai-tab-destinations";

// The tab saying it is somewhere to go. Uploads opens on the uploads list, so the tab beside it is
// only reachable by name if it says so itself. It has no filters of its own to reach.
declareAiTabDestination({
  id: "uploadsAuthorization.open",
  title: "Open the Uploads authorization tab",
  stateName: "app.uploads.authorization",
  opens: "page:uploadsAuthorization",
  description: "Opens Uploads > Authorization, where authorizations are uploaded from a file.",
  location: "the tab bar of Uploads",
  permissionKey: "view_uploads_section",
  examples: ["open the authorization upload"],
});

/**
 * What the Uploads list can do for the AI omnibar.
 *
 * Only the Uploads tab is described. The Authorization tab beside it has no filters at all - its
 * controls download an example and upload a file, which are changes rather than filters.
 */
declareAiCapability({
  id: "uploads.filter",
  title: "Filter the uploads list",
  effect: "prefill",
  description:
    "Sets the date range on Uploads, which filters the list of upload jobs. Only works while that tab is open.",
  location: "the date range of the Uploads page",
  scope: "page",
  permissionKey: "view_billing_section",
  params: [
    {
      name: "from",
      type: "date",
      required: false,
      description: "First day to include, as yyyy-MM-dd.",
    },
    {
      name: "to",
      type: "date",
      required: false,
      description: "Last day to include, as yyyy-MM-dd.",
    },
  ],
  examples: ["uploads from last week", "uploads this month"],
});

export function useUploadsAiCapabilities(params: {
  /** The page's own filter setter, typed loosely: this hook only sets keys it declares. */
  setFilter: (key: string, value: unknown) => void;
}): void {
  const { setFilter } = params;

  const setFilterRef = React.useRef(setFilter);
  setFilterRef.current = setFilter;

  useProvideAiCapability("uploads.filter", (args) => {
    if (args.from !== undefined) {
      setFilterRef.current("from", String(args.from));
    }

    if (args.to !== undefined) {
      setFilterRef.current("to", String(args.to));
    }

    const range = [args.from, args.to].filter(Boolean).join(" to ");

    return range === "" ? "Re-applied the upload filters" : `Showing uploads ${range}`;
  });
}
